import api from 'api';
import type {
    AdminConsultant,
    AdminConsultantRole,
    ConsultantLicense,
    ConsultantLicenseFormValues,
    ConsultantLicensePayload,
    ConsultantLicenseUpdatePayload,
    ConsultantProfileUpdatePayload,
    EditConsultantFormValues,
    ServiceConsultantCountryCode,
    ServiceProviderAdminRow,
    ServiceProviderConsultant,
} from 'types';

export const ADMIN_CONSULTANT_ROLES: AdminConsultantRole[] = ['CONSULTANT', 'ADMIN', 'SUPER_ADMIN'];

export const getAdminConsultantName = (consultant: AdminConsultant): string =>
    [consultant.firstname, consultant.middlename, consultant.lastname].filter(Boolean).join(' ') ||
    consultant.email;

export const getAdminConsultantRoleLabelKey = (role: AdminConsultantRole): string => {
    switch (role) {
        case 'SUPER_ADMIN':
            return 'superAdministrator';
        case 'ADMIN':
            return 'administrator';
        default:
            return 'consultant';
    }
};

export const toAdminConsultant = (
    consultant: ServiceProviderConsultant,
    provider: ServiceProviderAdminRow
): AdminConsultant => ({
    id: consultant.id,
    firstname: consultant.firstname,
    middlename: consultant.middlename,
    lastname: consultant.lastname,
    email: consultant.email,
    role: consultant.role,
    status: consultant.status,
    licenses: consultant.licenses,
    service_providers: [
        {
            service_provider_id: provider.id,
            company_name: provider.company_name,
            is_default_consultant: consultant.is_default_consultant,
        },
    ],
});

export const getConsultantProfileUpdate = (
    consultant: AdminConsultant,
    values: EditConsultantFormValues
): ConsultantProfileUpdatePayload | null => {
    const payload: ConsultantProfileUpdatePayload = {};
    const firstname = values.firstname.trim();
    const middlename = values.middlename.trim() || null;
    const lastname = values.lastname.trim();
    const email = values.email.trim().toLowerCase();

    if (firstname !== consultant.firstname) payload.firstname = firstname;
    if (middlename !== (consultant.middlename || null)) payload.middlename = middlename;
    if (lastname !== consultant.lastname) payload.lastname = lastname;
    if (email !== consultant.email.trim().toLowerCase()) payload.email = email;
    if (values.role && values.role !== consultant.role) payload.role = values.role;

    return Object.keys(payload).length ? payload : null;
};

export interface ConsultantLicenseEdits {
    create: ConsultantLicensePayload[];
    update: Array<{ id: number; payload: ConsultantLicenseUpdatePayload }>;
    removeIds: number[];
}

type ConsultantLicenseTarget = 'currentUser' | { serviceProviderId: number; consultantId: number };

const toLicensePayload = (
    license: EditConsultantFormValues['licenses'][number]
): ConsultantLicensePayload => ({
    country: license.country as ServiceConsultantCountryCode,
    license_number: license.license_number.trim(),
    date_issued: license.date_issued,
});

export const toLicenseFormValues = (
    licenses: ConsultantLicense[]
): ConsultantLicenseFormValues[] =>
    licenses.length
        ? licenses.map((license) => ({
              id: license.id,
              country: license.country,
              license_number: license.license_number,
              date_issued: license.date_issued ?? '',
          }))
        : [{ country: '', license_number: '', date_issued: '' }];

export const getConsultantLicenseEdits = (
    consultant: { licenses: ConsultantLicense[] },
    values: { licenses: ConsultantLicenseFormValues[] }
): ConsultantLicenseEdits => {
    const originalById = new Map(
        consultant.licenses.flatMap((license) =>
            license.id == null ? [] : [[license.id, license] as const]
        )
    );
    const retainedIds = new Set<number>();
    const create: ConsultantLicensePayload[] = [];
    const update: ConsultantLicenseEdits['update'] = [];

    values.licenses.forEach((license) => {
        const next = toLicensePayload(license);
        if (license.id == null || !originalById.has(license.id)) {
            create.push(next);
            return;
        }

        retainedIds.add(license.id);
        const original = originalById.get(license.id) as ConsultantLicense;
        const payload: ConsultantLicenseUpdatePayload = {};
        if (next.country !== original.country) payload.country = next.country;
        if (next.license_number !== original.license_number) {
            payload.license_number = next.license_number;
        }
        if (next.date_issued !== (original.date_issued ?? '')) {
            payload.date_issued = next.date_issued;
        }
        if (Object.keys(payload).length) update.push({ id: license.id, payload });
    });

    return {
        create,
        update,
        removeIds: [...originalById.keys()].filter((id) => !retainedIds.has(id)),
    };
};

/** Applies license edits with the consultant license endpoints and returns the saved rows. */
export const persistConsultantLicenseEdits = async (
    target: ConsultantLicenseTarget,
    current: ConsultantLicense[],
    edits: ConsultantLicenseEdits,
    nextValues: ConsultantLicenseFormValues[]
): Promise<ConsultantLicense[]> => {
    const originalLicenses = new Map(
        current.flatMap((license) => (license.id == null ? [] : [[license.id, license] as const]))
    );

    for (const licenseId of edits.removeIds) {
        if (target === 'currentUser') {
            await api.me.removeLicense(licenseId);
        } else {
            await api.serviceProvider.removeConsultantLicense(
                target.serviceProviderId,
                target.consultantId,
                licenseId
            );
        }
    }

    const updatedLicenses = new Map<number, ConsultantLicense>();
    for (const license of edits.update) {
        const response =
            target === 'currentUser'
                ? await api.me.updateLicense(license.id, license.payload)
                : await api.serviceProvider.updateConsultantLicense(
                      target.serviceProviderId,
                      target.consultantId,
                      license.id,
                      license.payload
                  );
        updatedLicenses.set(license.id, response.data);
    }

    const createdLicenses: ConsultantLicense[] = [];
    for (const license of edits.create) {
        const response =
            target === 'currentUser'
                ? await api.me.addLicense(license)
                : await api.serviceProvider.addConsultantLicense(
                      target.serviceProviderId,
                      target.consultantId,
                      license
                  );
        createdLicenses.push(response.data);
    }

    let createdIndex = 0;
    return nextValues.flatMap((license) => {
        if (license.id == null || !originalLicenses.has(license.id)) {
            const created = createdLicenses[createdIndex];
            createdIndex += 1;
            return created ? [created] : [];
        }
        const kept = updatedLicenses.get(license.id) ?? originalLicenses.get(license.id);
        return kept ? [kept] : [];
    });
};
